import React, { useState } from "react";
import { Typography, Box, useTheme, Divider, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../../global/AlertContext";
import { useGetApplicationByIdQuery, useUpdateApplicationMutation } from "../../../services/applicationService";
import LoadingComponent from "../../../components/LoadingComponent";
import ErrorComponent from "../../../components/ErrorComponent";
import SuccessButton from "../../../components/SuccessButton";
import Header from "../../../components/Header";
import { tokens } from "../../../theme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CancelButton from "../../../components/CancelButton";
import dayjs from 'dayjs'; // Import dayjs
import { useGetTicketsQuery } from "../../../services/ticketService";
import SearchSelect from "../../../components/SearchSelect";
import DosagesList from "../../../components/DosagesList";

const EditApplication = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [searchTermTickets, setSearchTermTickets] = useState("");
    const [selectedTicketId, setSelectedTicketId] = useState(null);
  

  const { data: applicationDetails, isLoading, error, refetch } = useGetApplicationByIdQuery(id);

  const { data: tickets, error: ticketsError, isLoading: isLoadingTickets } = useGetTicketsQuery({
    name: searchTermTickets,
  });

  const [updateApplication] = useUpdateApplicationMutation();
  const [boomCapacity, setBoomCapacity] = useState(2000);

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  const handleFormSubmit = async (values, { resetForm }) => {
    const updatedApplication = {
      programmed_date: values.programmed_date ? dayjs(values.programmed_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'), // Format as 'YYYY-MM-DD'
      ticket_id: values.ticket_id
    };
    try {
      await updateApplication({ id, updatedApplication }).unwrap();
      await refetch();
      resetForm();
      navigate("/applications");
      showAlert("Aplicación Actualizada!", "success");
    } catch (error) {
      showAlert(error.message, "error");
    }
  };

  const initialValues = {
    programmed_date: applicationDetails.programmed_date,
    ticket_id: "",
  };

  const applicationSchema = yup.object().shape({
    ticket_id: yup.string().required("Requerido"),
    programmed_date: yup.string().required("Requerido"),
  });

  return (
    <Box m="20px">
      <Header title="Ajustar Aplicación" />

      <Formik onSubmit={handleFormSubmit} initialValues={initialValues} validationSchema={applicationSchema}>
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px" gridTemplateColumns="repeat(2, minmax(0, 1fr))">

              {/* Ticket SearchSelect */}
              <Box sx={{ gridColumn: "span 1" }}>
                
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip 
                    title="El día del ticket no es tomado en cuenta al ajustar aplicación" 
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '0.9rem',
                          textAlign: 'center'
                        },
                      },
                    }}
                  >
                    <InfoIcon sx={{ color: colors.greenAccent[500], cursor: "pointer"}} />
                  </Tooltip>
                  <SearchSelect
                    label="Ticket"
                    options={tickets?.records || []}
                    value={tickets?.records.find((ticket) => ticket.id === values.ticket_id) || null}
                    onChange={(newValue) => {
                      setFieldValue("ticket_id", newValue ? newValue.id : "");
                      setSelectedTicketId(newValue ? newValue.id : null); // Update selected lote id
                    }}
                    onInputChange={(newInputValue) => {
                      setSearchTermTickets(newInputValue); // Update search term for tickets
                    }}
                    getOptionLabel={(option) => option.name}
                    error={!!touched.ticket_id && !!errors.ticket_id}
                    helperText={touched.ticket_id && errors.ticket_id}
                    isLoading={isLoadingTickets}
                    noOptionsText={ticketsError ? "Error al cargar tickets" : "No se encontraron tickets"}
                  />
                </Box>
              </Box>

              {/* Programmed Date */}
              <Box sx={{ gridColumn: "span 1" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Fecha programada"
                    value={values.programmed_date ? dayjs(values.programmed_date) : null} // Convert to Day.js object
                    onChange={(newValue) => setFieldValue("programmed_date", newValue)} // Set as Day.js object
                    format="DD/MM/YYYY" // Set user-friendly format
                    slotProps={{
                      textField: {
                        variant: "filled",
                        fullWidth: true,
                        error: !!touched.programmed_date && !!errors.programmed_date,
                        helperText: touched.programmed_date && errors.programmed_date
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>

            <Divider sx={{ my: "20px", borderColor: colors.grey[700] }} />

            <Box display="grid" gap="20px" gridTemplateColumns="repeat(6, minmax(0, 1fr))">
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Nombre de Aplicación
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Lote
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.lote_name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Bloques a aplicar
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.location}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Cantidad de Booms
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.total_area * (2000 / boomCapacity)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Hectáreas
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.total_area}
                </Typography>
              </Box>

              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel id="boom-capacity-label" sx={{ color: colors.grey[100] }}>
                    Capacidad del Boom
                  </InputLabel>
                  <Select
                    labelId="boom-capacity-label"
                    value={boomCapacity}
                    label="Capacidad"
                    onChange={(e) => setBoomCapacity(e.target.value)}
                    sx={{
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      width: "120px",
                      "& .MuiSelect-select": {
                        padding: "8px 12px",
                      },
                    }}
                  >
                    <MenuItem value={2000}>2000 L</MenuItem>
                    <MenuItem value={4000}>4000 L</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Divider sx={{ my: "20px", borderColor: colors.grey[700] }} />

            <Typography variant="h5" fontWeight="bold" mb="16px" color={colors.grey[100]}>
              Dosificación
            </Typography>
            <DosagesList ticketId={selectedTicketId} isReadOnly />

            <Box
              display="flex"
              justifyContent="end"
              mt="2vh"
              gap="4px"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/applications')}/>
              <SuccessButton label="Ajustar Aplicación" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditApplication;